import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { FormProps } from '../helpers';
import ListToggle from '../list-toggle';
import { UiOption } from '@/lib/helpers';
import { Box, Stack } from '@mui/material';
import useQueryHelper from '@/hooks/use-query-helper';
import { QueryKey } from '@/lib/query-client';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import Icon from '@/components/icon';
import Tooltip from '@/components/tooltip';
import { getMcpToolOptions, McpEntity } from '@/lib/models/policy-model/mcp-controls';
import { McpToolModel } from '@/lib/models/mcp-tool/mcp-tool.model';
import { useQuery } from 'react-query';
import { getAllMcpServers, getAllMcpTools } from '@/lib/services/mcp.service';
import McpToolButton from '@/components/mcp-tool-button';
import { McpServerModel } from '@/lib/models/mcp-server.model';
import useDrawer from '@/hooks/use-drawer.hook';
import { buildToolAccess } from './helpers';

interface Props extends FormProps {
  value: McpEntity[] | undefined;
  onChange: (value: McpEntity[] | undefined) => void;
  aclVariant?: 'allow' | 'block';
}

const useStyles = createUseStyles({
  mcpToolsList: {
    '& label.MuiFormControlLabel-root': {
      alignItems: 'flex-start',
      marginRight: 0,
    },
    '& span.MuiFormControlLabel-label': {
      display: 'block',
      width: '100%',
    },
    '& .MuiTypography-root': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      flexGrow: 1,
    },
  },
});

const McpToolsAcl: FC<Props> = ({ aclVariant = 'allow', value, onChange, ...props }) => {
  const styles = useStyles();
  const isAllowList = aclVariant === 'allow';

  const { openDrawer } = useDrawer();

  const handleView = useCallback(
    (event: MouseEvent, { id: defaultToolId, serverId }: McpToolModel) => {
      event.stopPropagation(); // prevent clicking on info from opening the button accordion
      event.preventDefault();
      // Pass tab as a prop so it gets set in the URL before the drawer mounts
      openDrawer('mcp-server', { id: serverId, defaultToolId, tab: 'tools' });
    },
    [openDrawer]
  );

  const query = useQuery([QueryKey.McpToolList], async () => getAllMcpTools());
  const { data = [] } = query;
  const { showLoader: toolsLoading } = useQueryHelper(query);

  const serverQuery = useQuery([QueryKey.McpServersList], async () => getAllMcpServers());
  const { data: mcpServers } = serverQuery;
  const { showLoader: serversLoading } = useQueryHelper(serverQuery);

  const serverMap: Map<string, McpServerModel> = useMemo(() => {
    const map: Map<string, McpServerModel> = new Map();
    mcpServers?.forEach((server) => {
      map.set(server.id, server);
    });
    return map;
  }, [mcpServers]);

  const toolIds = useMemo(() => {
    return (value || []).filter(({ action }) => action === aclVariant).map(({ toolId }) => toolId);
  }, [value, aclVariant]);

  const onListToggleChange = useCallback(
    (option: UiOption[]) => {
      const selectedToolIds = option.map(({ value }) => value);
      const updated = buildToolAccess(value, selectedToolIds, aclVariant);
      onChange(updated.length ? updated : undefined);
    },
    [value, onChange, aclVariant]
  );

  const toolOptions: UiOption[] = useMemo(() => {
    return getMcpToolOptions(
      data,
      aclVariant,
      new PolicyModel({ mcpControls: { toolAccess: value } })
    ).map(({ value, meta }) => {
      const { mcpTool, disabledByAlternateList } = meta as {
        mcpTool: McpToolModel;
        disabledByAlternateList: boolean;
      };

      const server = serverMap.get(mcpTool.serverId);
      const disabled = disabledByAlternateList;

      const LabelEl = (
        <Tooltip
          title={
            isAllowList
              ? 'This tool is on the block list and cannot be allowed.'
              : 'This tool is on the allow list and cannot be blocked.'
          }
          disabled={!disabled}
        >
          <Stack direction="row" alignItems="center" width="100%" gap={1}>
            {disabled && <Icon name="lock" size="small" color="grey" />}
            <McpToolButton mcpTool={mcpTool} mcpServer={server} onInfoClick={handleView} />
          </Stack>
        </Tooltip>
      );

      return {
        label: LabelEl,
        value,
        meta: { keyword: `${server?.title ?? ''} ${mcpTool.name} ${mcpTool.description}` },
        disabled,
      };
    });
  }, [data, handleView, serverMap, value, aclVariant, isAllowList]);

  const isLoading = toolsLoading || serversLoading;

  const rightTitle = (
    <Stack direction="row" alignItems="center" gap={1}>
      <Icon name={isAllowList ? 'check' : 'block'} size="small" />
      {isAllowList ? 'Allowed MCP Tools' : 'Blocked MCP Tools'}
    </Stack>
  );

  return (
    <Box className={styles.mcpToolsList}>
      <ListToggle
        {...props}
        value={toolIds}
        onChange={onListToggleChange}
        label="MCP Tools"
        leftTitle="Available MCP Tools"
        rightTitle={rightTitle}
        options={toolOptions}
        labelChecks={true}
        loadState={isLoading ? 'loading' : 'loaded'}
        rowHeight={70}
        searchPlaceholder="Search by tool or server name"
      />
    </Box>
  );
};

export default McpToolsAcl;
